import {
  getLeoUuid,
  LeoUUID,
  getString,
  getObject,
  getBoolean,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { RespondentQuestionDetails, RespondentQuestionDetailsDTO } from "./respondentQuestionDetails";
import { ProgressPercentage, ProgressPercentageDTO } from "./progressPercentage";
import { SurveyLocalizedText, SurveyLocalizedTextDTO } from "../assets/surveyLocalizedText";
/* eslint-disable import/export */
export abstract class BacktrackAndGetPreviousQuestionRPC implements
  LeoRPC<BacktrackAndGetPreviousQuestionRPC.Request, BacktrackAndGetPreviousQuestionRPC.Response, BacktrackAndGetPreviousQuestionRPC.Errors.Errors>
{
  abstract execute(
    request: BacktrackAndGetPreviousQuestionRPC.Request
  ): Promise<LeoRPCResult<BacktrackAndGetPreviousQuestionRPC.Response, BacktrackAndGetPreviousQuestionRPC.Errors.Errors>>;
}

export namespace BacktrackAndGetPreviousQuestionRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    currentQuestionId: string;
    surveyLinkId: string;
    partialAnswerJson: string;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isBacktrackAndGetPreviousQuestionRPCRequest: boolean = true;
  
    readonly currentQuestionId: LeoUUID;
    readonly surveyLinkId: LeoUUID;
    readonly partialAnswerJson: string;
  
  
    constructor(
    currentQuestionId: LeoUUID, 
    surveyLinkId: LeoUUID, 
    partialAnswerJson: string
    ) {
      this.currentQuestionId = currentQuestionId;
      this.surveyLinkId = surveyLinkId;
      Request.validatePartialAnswerJson(partialAnswerJson);
      this.partialAnswerJson = partialAnswerJson;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const currentQuestionId = getLeoUuid(dto, "currentQuestionId", InvalidRequestCurrentQuestionIdError);
      const surveyLinkId = getLeoUuid(dto, "surveyLinkId", InvalidRequestSurveyLinkIdError);
      const partialAnswerJson = getString(dto, "partialAnswerJson", InvalidRequestPartialAnswerJsonError);
      return new Request(
        currentQuestionId,
        surveyLinkId,
        partialAnswerJson
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          currentQuestionId: this.currentQuestionId.toDTO(),
          surveyLinkId: this.surveyLinkId.toDTO(),
          partialAnswerJson: this.partialAnswerJson
      };
      return dto;
    }
    private static validatePartialAnswerJson = (partialAnswerJson: string): void => {
      if(partialAnswerJson.trim().length === 0) {
        throw new InvalidRequestPartialAnswerJsonNonEmptyError(
          `Attribute partialAnswerJson cannot be an empty string.`
        );
      }
    }
    
  }
  
  Request.prototype.toString = function toString(): string {
    return `BacktrackAndGetPreviousQuestionRPC(currentQuestionId='${this.currentQuestionId}',surveyLinkId='${this.surveyLinkId}',partialAnswerJson='${this.partialAnswerJson}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {
    previousQuestionDetails: RespondentQuestionDetailsDTO;
    isFirstQuestion: boolean;
    progressPercentage: ProgressPercentageDTO;
    answerJson: string;
  }
  
  export class Response implements LeoRPCResponse {
    readonly isBacktrackAndGetPreviousQuestionResponse: boolean = true;
  
    readonly previousQuestionDetails: RespondentQuestionDetails;
    readonly isFirstQuestion: boolean;
    readonly progressPercentage: ProgressPercentage;
    readonly answerJson: string;
  
  
    constructor(
    previousQuestionDetails: RespondentQuestionDetails, 
    isFirstQuestion: boolean, 
    progressPercentage: ProgressPercentage, 
    answerJson: string
    ) {
      this.previousQuestionDetails = previousQuestionDetails;
      this.isFirstQuestion = isFirstQuestion;
      this.progressPercentage = progressPercentage;
      Response.validateAnswerJson(answerJson);
      this.answerJson = answerJson;
    }
  
    static fromDTO(dto: ResponseDTO): Response {
      const previousQuestionDetails = RespondentQuestionDetails.fromDTO(getObject(dto, "previousQuestionDetails", InvalidResponsePreviousQuestionDetailsError));
      const isFirstQuestion = getBoolean(dto, "isFirstQuestion", InvalidResponseIsFirstQuestionError);
      const progressPercentage = ProgressPercentage.fromDTO(getObject(dto, "progressPercentage", InvalidResponseProgressPercentageError));
      const answerJson = getString(dto, "answerJson", InvalidResponseAnswerJsonError);
      return new Response(
        previousQuestionDetails,
        isFirstQuestion,
        progressPercentage,
        answerJson
      );
    }
  
    toDTO(): ResponseDTO {
      const dto: ResponseDTO = {
          previousQuestionDetails: this.previousQuestionDetails.toDTO(),
          isFirstQuestion: this.isFirstQuestion,
          progressPercentage: this.progressPercentage.toDTO(),
          answerJson: this.answerJson
      };
      return dto;
    }
    private static validateAnswerJson = (answerJson: string): void => {
      if(answerJson.trim().length === 0) {
        throw new InvalidResponseAnswerJsonNonEmptyError(
          `Attribute answerJson cannot be an empty string.`
        );
      }
    }
    
  }
  
  Response.prototype.toString = function toString(): string {
    return `BacktrackAndGetPreviousQuestion(previousQuestionDetails='${this.previousQuestionDetails}',isFirstQuestion='${this.isFirstQuestion}',progressPercentage='${this.progressPercentage}',answerJson='${this.answerJson}')`;
  };
  

  export enum RPCError {
    InvalidSurveyLinkId = "INVALID_SURVEY_LINK_ID",
    InvalidQuestionId = "INVALID_QUESTION_ID",
    PreviousQuestionNotFound = "PREVIOUS_QUESTION_NOT_FOUND",
    SurveyIsClosed = "SURVEY_IS_CLOSED",
    SurveyAlreadySubmitted = "SURVEY_ALREADY_SUBMITTED",
    InvalidAnswerJson = "INVALID_ANSWER_JSON"
  }

  export namespace Errors {
    
    export interface InvalidSurveyLinkIdDTO {
      code: string;
    }
    
    export class InvalidSurveyLinkId extends LeoRPCError<RPCError> {
      readonly isInvalidSurveyLinkId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidSurveyLinkId;
        super(code);
      }
    
    
      toDTO(): InvalidSurveyLinkIdDTO {
        const dto: InvalidSurveyLinkIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidSurveyLinkId.prototype.toString = function toString(): string {
      return `BacktrackAndGetPreviousQuestionRPC.INVALID_SURVEY_LINK_ID()`;
    };
    
    
    export interface InvalidQuestionIdDTO {
      code: string;
    }
    
    export class InvalidQuestionId extends LeoRPCError<RPCError> {
      readonly isInvalidQuestionId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidQuestionId;
        super(code);
      }
    
    
      toDTO(): InvalidQuestionIdDTO {
        const dto: InvalidQuestionIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidQuestionId.prototype.toString = function toString(): string {
      return `BacktrackAndGetPreviousQuestionRPC.INVALID_QUESTION_ID()`;
    };
    
    
    export interface PreviousQuestionNotFoundDTO {
      code: string;
    }
    
    export class PreviousQuestionNotFound extends LeoRPCError<RPCError> {
      readonly isPreviousQuestionNotFound: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.PreviousQuestionNotFound;
        super(code);
      }
    
    
      toDTO(): PreviousQuestionNotFoundDTO {
        const dto: PreviousQuestionNotFoundDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    PreviousQuestionNotFound.prototype.toString = function toString(): string {
      return `BacktrackAndGetPreviousQuestionRPC.PREVIOUS_QUESTION_NOT_FOUND()`;
    };
    
    
    export interface SurveyIsClosedDTO {
      code: string;
      surveyIsClosedTitleText: SurveyLocalizedTextDTO;
      surveyIsClosedDescriptionText: SurveyLocalizedTextDTO;
    }
    
    export class SurveyIsClosed extends LeoRPCError<RPCError> {
      readonly isSurveyIsClosed: boolean = true;
    
      readonly surveyIsClosedTitleText: SurveyLocalizedText;
      readonly surveyIsClosedDescriptionText: SurveyLocalizedText;
    
      constructor(
      surveyIsClosedTitleText: SurveyLocalizedText, 
      surveyIsClosedDescriptionText: SurveyLocalizedText
      ) {
        const code = RPCError.SurveyIsClosed;
        super(code);
        this.surveyIsClosedTitleText = surveyIsClosedTitleText;
        this.surveyIsClosedDescriptionText = surveyIsClosedDescriptionText;
      }
    
      static fromDTO(dto: SurveyIsClosedDTO): SurveyIsClosed {
        const surveyIsClosedTitleText = SurveyLocalizedText.fromDTO(getObject(dto, "surveyIsClosedTitleText", InvalidResponseSurveyIsClosedSurveyIsClosedTitleTextError));
        const surveyIsClosedDescriptionText = SurveyLocalizedText.fromDTO(getObject(dto, "surveyIsClosedDescriptionText", InvalidResponseSurveyIsClosedSurveyIsClosedDescriptionTextError));
        return new SurveyIsClosed(
          surveyIsClosedTitleText,
          surveyIsClosedDescriptionText
        );
      }
    
      toDTO(): SurveyIsClosedDTO {
        const dto: SurveyIsClosedDTO = {
          code: this.code,
          surveyIsClosedTitleText: this.surveyIsClosedTitleText.toDTO(),
          surveyIsClosedDescriptionText: this.surveyIsClosedDescriptionText.toDTO()
        };
        return dto;
      }
    
      }
    
    SurveyIsClosed.prototype.toString = function toString(): string {
      return `BacktrackAndGetPreviousQuestionRPC.SURVEY_IS_CLOSED(surveyIsClosedTitleText='${this.surveyIsClosedTitleText}',surveyIsClosedDescriptionText='${this.surveyIsClosedDescriptionText}')`;
    };
    
    
    export interface SurveyAlreadySubmittedDTO {
      code: string;
      thankYouPageText: SurveyLocalizedTextDTO;
    }
    
    export class SurveyAlreadySubmitted extends LeoRPCError<RPCError> {
      readonly isSurveyAlreadySubmitted: boolean = true;
    
      readonly thankYouPageText: SurveyLocalizedText;
    
      constructor(
      thankYouPageText: SurveyLocalizedText
      ) {
        const code = RPCError.SurveyAlreadySubmitted;
        super(code);
        this.thankYouPageText = thankYouPageText;
      }
    
      static fromDTO(dto: SurveyAlreadySubmittedDTO): SurveyAlreadySubmitted {
        const thankYouPageText = SurveyLocalizedText.fromDTO(getObject(dto, "thankYouPageText", InvalidResponseSurveyAlreadySubmittedThankYouPageTextError));
        return new SurveyAlreadySubmitted(
          thankYouPageText
        );
      }
    
      toDTO(): SurveyAlreadySubmittedDTO {
        const dto: SurveyAlreadySubmittedDTO = {
          code: this.code,
          thankYouPageText: this.thankYouPageText.toDTO()
        };
        return dto;
      }
    
      }
    
    SurveyAlreadySubmitted.prototype.toString = function toString(): string {
      return `BacktrackAndGetPreviousQuestionRPC.SURVEY_ALREADY_SUBMITTED(thankYouPageText='${this.thankYouPageText}')`;
    };
    
    
    export interface InvalidAnswerJsonDTO {
      code: string;
    }
    
    export class InvalidAnswerJson extends LeoRPCError<RPCError> {
      readonly isInvalidAnswerJson: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidAnswerJson;
        super(code);
      }
    
    
      toDTO(): InvalidAnswerJsonDTO {
        const dto: InvalidAnswerJsonDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidAnswerJson.prototype.toString = function toString(): string {
      return `BacktrackAndGetPreviousQuestionRPC.INVALID_ANSWER_JSON()`;
    };
    

    export type Errors = InvalidSurveyLinkId | InvalidQuestionId | PreviousQuestionNotFound | SurveyIsClosed | SurveyAlreadySubmitted | InvalidAnswerJson;
    export type ErrorsDTO = InvalidSurveyLinkIdDTO | InvalidQuestionIdDTO | PreviousQuestionNotFoundDTO | SurveyIsClosedDTO | SurveyAlreadySubmittedDTO | InvalidAnswerJsonDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidSurveyLinkId:
          return new InvalidSurveyLinkId();
        case RPCError.InvalidQuestionId:
          return new InvalidQuestionId();
        case RPCError.PreviousQuestionNotFound:
          return new PreviousQuestionNotFound();
        case RPCError.SurveyIsClosed:
          return SurveyIsClosed.fromDTO(dto as SurveyIsClosedDTO);
        case RPCError.SurveyAlreadySubmitted:
          return SurveyAlreadySubmitted.fromDTO(dto as SurveyAlreadySubmittedDTO);
        case RPCError.InvalidAnswerJson:
          return new InvalidAnswerJson();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestCurrentQuestionIdError extends BacktrackAndGetPreviousQuestionRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestCurrentQuestionIdError";
  }
}

export class InvalidRequestSurveyLinkIdError extends BacktrackAndGetPreviousQuestionRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSurveyLinkIdError";
  }
}

export class InvalidRequestPartialAnswerJsonError extends BacktrackAndGetPreviousQuestionRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestPartialAnswerJsonError";
  }
}

export class InvalidRequestPartialAnswerJsonNonEmptyError extends InvalidRequestPartialAnswerJsonError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestPartialAnswerJsonNonEmptyError";
  }
}

export class InvalidResponsePreviousQuestionDetailsError extends BacktrackAndGetPreviousQuestionRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponsePreviousQuestionDetailsError";
  }
}

export class InvalidResponseIsFirstQuestionError extends BacktrackAndGetPreviousQuestionRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseIsFirstQuestionError";
  }
}

export class InvalidResponseProgressPercentageError extends BacktrackAndGetPreviousQuestionRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseProgressPercentageError";
  }
}

export class InvalidResponseAnswerJsonError extends BacktrackAndGetPreviousQuestionRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseAnswerJsonError";
  }
}

export class InvalidResponseAnswerJsonNonEmptyError extends InvalidResponseAnswerJsonError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseAnswerJsonNonEmptyError";
  }
}

export class InvalidResponseInvalidSurveyLinkIdError extends BacktrackAndGetPreviousQuestionRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidSurveyLinkIdError";
  }
}

export class InvalidResponseInvalidQuestionIdError extends BacktrackAndGetPreviousQuestionRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidQuestionIdError";
  }
}

export class InvalidResponsePreviousQuestionNotFoundError extends BacktrackAndGetPreviousQuestionRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponsePreviousQuestionNotFoundError";
  }
}

export class InvalidResponseSurveyIsClosedError extends BacktrackAndGetPreviousQuestionRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyIsClosedError";
  }
}

export class InvalidResponseSurveyIsClosedSurveyIsClosedTitleTextError extends InvalidResponseSurveyIsClosedError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyIsClosedSurveyIsClosedTitleTextError";
  }
}

export class InvalidResponseSurveyIsClosedSurveyIsClosedDescriptionTextError extends InvalidResponseSurveyIsClosedError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyIsClosedSurveyIsClosedDescriptionTextError";
  }
}
export class InvalidResponseSurveyAlreadySubmittedError extends BacktrackAndGetPreviousQuestionRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyAlreadySubmittedError";
  }
}

export class InvalidResponseSurveyAlreadySubmittedThankYouPageTextError extends InvalidResponseSurveyAlreadySubmittedError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyAlreadySubmittedThankYouPageTextError";
  }
}
export class InvalidResponseInvalidAnswerJsonError extends BacktrackAndGetPreviousQuestionRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidAnswerJsonError";
  }
}

